import React, { useEffect, useState, useMemo, useContext, createContext, useRef } from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { get, patch, doDelete } from '../../../helpers/httpClient';
import { toast } from 'react-toastify';
import { GrGallery, GrDocumentText, GrCatalogOption } from "react-icons/gr";
import { FiChevronRight, FiMenu, FiEdit2 } from "react-icons/fi";

function getTypeIcon(t) {
  const type = (t || '').toLowerCase();
  switch (type) {
    case 'article': return <GrDocumentText size={13} aria-label="article" />;
    case 'catalog': return <GrCatalogOption size={13} aria-label="catalog" />;
    case 'gallery':
    default: return <GrGallery size={13} aria-label='gallery' />;
  }
}

const keyFor = (parent) => (parent === null || parent === undefined || parent === '' ? '__root__' : parent);

const TreeCtx = createContext(null);

function NodeRow({ p, level }) {
  const {
    pagesByParent, expanded, onToggle, onEdit, onAdd,
    confirmingId, setConfirmingId, deleting, handleDelete,
    dragId, overId, overPos, nodeRefs,
    onDragStart, onDragEnter, onDragOver, onDrop, onDragEnd,
  } = useContext(TreeCtx);

  const type = (p.type || '').toLowerCase();
  const isBranch = type === 'catalog' || type === 'gallery';
  const childCount = (pagesByParent[keyFor(p.id)] || []).length;
  const isOpen = !!expanded[p.id];
  const isConfirming = confirmingId === p.id;
  const isDragging = dragId === p.id;
  const isDragBefore = overId === p.id && overPos === 'before' && dragId !== p.id;
  const isDragAfter = overId === p.id && overPos === 'after' && dragId !== p.id;
  const typeLabel = type === 'gallery-item' ? 'item' : (type || 'page');

  return (
    <div
      ref={el => { if (el) nodeRefs.current[p.id] = el; else delete nodeRefs.current[p.id]; }}
      className={[
        'structure-node',
        `structure-node--${type}`,
        isOpen ? 'structure-node--open' : '',
        isDragging ? 'structure-node--dragging' : '',
        isDragBefore ? 'structure-node--drag-before' : '',
        isDragAfter ? 'structure-node--drag-after' : '',
      ].filter(Boolean).join(' ')}
    >
      <div
        className="structure-node-header"
        draggable
        onDragStart={e => onDragStart(p.id, keyFor(p.parent), e)}
        onDragEnter={e => onDragEnter(p.id, keyFor(p.parent), e)}
        onDragOver={e => onDragOver(p.id, keyFor(p.parent), e)}
        onDrop={e => onDrop(p.id, keyFor(p.parent), e)}
        onDragEnd={onDragEnd}
      >
        <div className="structure-node-left">
          <span className="structure-drag-handle" aria-hidden>
            <FiMenu size={12} />
          </span>
          {isBranch ? (
            <button
              type="button"
              className="structure-chevron-btn"
              onClick={() => onToggle(p.id)}
              title={isOpen ? 'Collapse' : 'Expand'}
              aria-label={isOpen ? 'Collapse children' : 'Expand children'}
            >
              <FiChevronRight size={14} className="structure-chevron" />
            </button>
          ) : (
            <span className="structure-leaf-dot" aria-hidden />
          )}
          <span className="structure-type-icon">{getTypeIcon(p.type)}</span>
          <span className="structure-type-label">{typeLabel}</span>
          <span className="structure-node-slug">/{p.slug}</span>
          {p.caption ? <span className="structure-node-caption">{p.caption}</span> : <span className="structure-node-title">{p.title}</span>}
          {isBranch && !isOpen && childCount > 0 && (
            <span className="structure-child-count">{childCount}</span>
          )}
        </div>
        <div className="structure-node-actions">
          {isConfirming ? (
            <>
              <span className="structure-delete-label">Delete?</span>
              <button
                type="button"
                className="structure-confirm-btn structure-confirm-btn--yes"
                onClick={() => handleDelete(p.id)}
                disabled={deleting}
              >
                {deleting ? <Spinner animation="border" size="sm" /> : 'Yes'}
              </button>
              <button
                type="button"
                className="structure-confirm-btn structure-confirm-btn--no"
                onClick={() => setConfirmingId(null)}
                disabled={deleting}
              >
                No
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="structure-edit-btn"
                title="Edit page"
                onClick={() => onEdit(p.id)}
              ><FiEdit2 size={13} /></button>
              <button
                type="button"
                className="structure-delete-btn"
                title="Delete page"
                onClick={() => setConfirmingId(p.id)}
              >×</button>
            </>
          )}
        </div>
      </div>
      {isBranch && isOpen && (
        <div className="structure-node-children">
          <TreeLevel parentSlug={p.id} level={level + 1} />
          <button
            type="button"
            className="structure-add-child-btn"
            onClick={() => onAdd(p.type, p.id)}
          >+ Add child</button>
        </div>
      )}
    </div>
  );
}

function TreeLevel({ parentSlug, level = 0 }) {
  const { pagesByParent } = useContext(TreeCtx);
  const nodes = pagesByParent[keyFor(parentSlug)] || [];
  if (nodes.length === 0) return null;
  return (
    <div>
      {nodes.map((p) => (
        <NodeRow key={p.id} p={p} level={level} />
      ))}
    </div>
  );
}

function StructureTree({
  pages, expanded, onToggle, onEdit, onAdd,
  confirmingId, setConfirmingId, deleting, handleDelete,
  dragId, overId, overPos, nodeRefs,
  onDragStart, onDragEnter, onDragOver, onDrop, onDragEnd,
}) {
  const pagesByParent = useMemo(() => {
    const map = {};
    for (const pg of pages || []) {
      const k = keyFor(pg.parent);
      if (!map[k]) map[k] = [];
      map[k].push(pg);
    }
    Object.values(map).forEach(arr =>
      arr.sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.id.localeCompare(b.id))
    );
    return map;
  }, [pages]);

  return (
    <TreeCtx.Provider value={{
      pagesByParent, expanded, onToggle, onEdit, onAdd,
      confirmingId, setConfirmingId, deleting, handleDelete,
      dragId, overId, overPos, nodeRefs,
      onDragStart, onDragEnter, onDragOver, onDrop, onDragEnd,
    }}>
      <TreeLevel parentSlug={null} level={0} />
    </TreeCtx.Provider>
  );
}

function Structure({ website }) {
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(false);

  const storageKey = website?.id ? `structure_expanded_${website.id}` : null;
  const [expanded, setExpanded] = useState(() => {
    if (!storageKey) return {};
    try { return JSON.parse(localStorage.getItem(storageKey)) || {}; } catch { return {}; }
  });
  useEffect(() => {
    if (storageKey) localStorage.setItem(storageKey, JSON.stringify(expanded));
  }, [expanded, storageKey]);

  const [confirmingId, setConfirmingId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Drag-and-drop state
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [overPos, setOverPos] = useState(null);
  const nodeRefs = useRef({});
  const activeDrag = useRef({ id: null, parentKey: null });

  useEffect(() => {
    let isMounted = true;
    async function loadPages() {
      if (!website?.id) return;
      try {
        setLoading(true);
        const resp = await get(`websites/${website.id}/pages`);
        const list = Array.isArray(resp) ? resp : (resp?.pages || []);
        list.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        if (isMounted) setPages(list);
      } catch (e) {
        if (isMounted) toast.error('Failed to load pages.');
      } finally {
        if (isMounted) setLoading(false);
      }
    }
    loadPages();
    return () => { isMounted = false; };
  }, [website?.id]);

  if (!website) return <Alert variant="warning">Website not found.</Alert>;

  const goToCreate = () => navigate(`/websites/${website.id}/structure/new`);
  const goToEdit = (id) => navigate(`/websites/${website.id}/structure/${id}/edit`);
  const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const expandAll = () => {
    const ids = pages.reduce((acc, p) => {
      const t = (p.type || '').toLowerCase();
      if (t === 'catalog' || t === 'gallery') acc[p.id] = true;
      return acc;
    }, {});
    setExpanded(ids);
  };
  const collapseAll = () => setExpanded({});

  const addEntity = (parentType = null, parentId = null) => {
    let parentParam = parentId ? `?parent=${encodeURIComponent(parentId)}` : '';
    if (parentType && parentType.toLowerCase() === 'gallery') parentParam += '&type=gallery-item';
    navigate(`/websites/${website.id}/structure/new${parentParam}`);
  };

  const handleDelete = async (id) => {
    try {
      setDeleting(true);
      await doDelete(`websites/${website.id}/pages/${id}`);
      setPages(prev => prev.filter(p => p.id !== id));
      setConfirmingId(null);
      toast.success('Page deleted.');
    } catch (e) {
      toast.error('Failed to delete page.');
    } finally {
      setDeleting(false);
    }
  };

  const handleReorder = (draggedId, targetId, pos, parentKey) => {
    const siblings = [...pages.filter(p => keyFor(p.parent) === parentKey)]
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const others = pages.filter(p => keyFor(p.parent) !== parentKey);

    const fromIdx = siblings.findIndex(p => p.id === draggedId);
    let toIdx = siblings.findIndex(p => p.id === targetId);
    if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return;

    const [moved] = siblings.splice(fromIdx, 1);
    if (fromIdx < toIdx) toIdx--;
    siblings.splice(pos === 'before' ? toIdx : toIdx + 1, 0, moved);

    const reordered = siblings.map((p, i) => ({ ...p, order: i }));
    setPages([...others, ...reordered]);

    reordered.forEach(p => {
      const orig = pages.find(o => o.id === p.id);
      if (!orig || orig.order !== p.order) {
        patch(`websites/${website.id}/pages/${p.id}`, { order: p.order })
          .catch(() => toast.error('Failed to save order.'));
      }
    });
  };

  const clearDrag = () => {
    activeDrag.current = { id: null, parentKey: null };
    setDragId(null);
    setOverId(null);
    setOverPos(null);
  };

  const onDragStart = (id, parentKey, e) => {
    e.stopPropagation();
    activeDrag.current = { id, parentKey };
    setDragId(id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', id);
  };

  const onDragEnter = (id, parentKey, e) => {
    e.stopPropagation();
    if (activeDrag.current.parentKey !== parentKey) return;
    setOverId(id);
  };

  const onDragOver = (id, parentKey, e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!activeDrag.current.id || activeDrag.current.parentKey !== parentKey) {
      e.dataTransfer.dropEffect = 'none';
      return;
    }
    e.dataTransfer.dropEffect = 'move';
    const el = nodeRefs.current[id];
    if (el) {
      const rect = el.getBoundingClientRect();
      const pos = (e.clientY - rect.top) / rect.height < 0.5 ? 'before' : 'after';
      setOverId(id);
      setOverPos(pos);
    }
  };

  const onDrop = (id, parentKey, e) => {
    e.stopPropagation();
    e.preventDefault();
    const { id: dId, parentKey: dParent } = activeDrag.current;
    if (!dId || dParent !== parentKey || dId === id) { clearDrag(); return; }
    handleReorder(dId, id, overPos || 'after', parentKey);
    clearDrag();
  };

  const onDragEnd = () => clearDrag();

  const hasBranches = pages.some(p => {
    const t = (p.type || '').toLowerCase();
    return t === 'catalog' || t === 'gallery';
  });

  return (
    <div>
      <div className="mt-3">
        <h5 className="mb-0">Pages</h5>
        <p className="muted-hint mt-1">
          Manage your site's pages. Use the tree below to navigate and edit.
        </p>
        <div className="structure-controls mb-3">
          <button type="button" className="structure-add-child-btn" onClick={goToCreate}>+ Add page</button>
          {hasBranches && pages.length > 0 && (
            <>
              <span className="structure-controls-sep">·</span>
              <button type="button" className="structure-controls-btn" onClick={expandAll}>Expand all</button>
              <span className="structure-controls-sep">·</span>
              <button type="button" className="structure-controls-btn" onClick={collapseAll}>Collapse all</button>
            </>
          )}
        </div>
        {loading ? (
          <div className="text-center my-3"><Spinner animation="border" size="sm" /></div>
        ) : pages.length === 0 ? (
          <div className="structure-empty">
            <div className="structure-empty-icon"><GrDocumentText /></div>
            <div className="structure-empty-title">No pages yet</div>
            <div className="structure-empty-hint">Use the Add button to create your first content entity.</div>
          </div>
        ) : (
          <StructureTree
            pages={pages}
            expanded={expanded}
            onToggle={toggleExpand}
            onEdit={goToEdit}
            onAdd={addEntity}
            confirmingId={confirmingId}
            setConfirmingId={setConfirmingId}
            deleting={deleting}
            handleDelete={handleDelete}
            dragId={dragId}
            overId={overId}
            overPos={overPos}
            nodeRefs={nodeRefs}
            onDragStart={onDragStart}
            onDragEnter={onDragEnter}
            onDragOver={onDragOver}
            onDrop={onDrop}
            onDragEnd={onDragEnd}
          />
        )}
      </div>
    </div>
  );
}

export default Structure;
